/*
 * DAC slideshow logo reveal (sections/slideshow-dac.liquid + the "DAC slideshow logo" block in theme.css).
 *
 * The element holds two copies of the logo (left half / right half). It waits for the slideshow's first
 * "carousel:settle" event, then adds .is-ready, which attaches the CSS animation that slides both halves out from
 * the centre line (same trigger as wild eyes). If the event never arrives (a single slide, the theme editor
 * re-rendering the section) a timer starts it anyway, so the logo is never left hidden.
 * When the halves have finished, .is-done swaps them for one whole copy of the logo, so there is no join line.
 */
if (!window.customElements.get('dac-slideshow-logo')) {
  class DacSlideshowLogo extends HTMLElement {
    #lifecycle = null;
    #timer = null;
    #started = false;

    connectedCallback() {
      this.#lifecycle = new AbortController();

      const carousel = this.closest('slideshow-carousel');

      if (!carousel) {
        this.#start();
        return;
      }

      this.#trackContent(carousel);

      // This script is deferred: the slideshow may already have settled (the section's inline script left a flag)
      if (carousel.hasAttribute('data-dac-settled')) {
        this.#start();
        return;
      }

      carousel.addEventListener('carousel:settle', () => this.#start(), { once: true, signal: this.#lifecycle.signal });

      // Fallback: never leave the logo waiting for an event that is not coming
      this.#timer = window.setTimeout(() => this.#start(), 2500);
    }

    // On a phone the logo is anchored from the slide bottom, the text from the slide bottom too, but the text grows upwards
    // when the title wraps onto another line (narrow phone, long title, font swap). Publish how tall the tallest slide's
    // text + buttons are as --dac-slideshow-logo-clearance so the css can keep the logo above them.
    #trackContent(carousel) {
      if (typeof ResizeObserver !== 'function') {
        return;
      }

      const update = () => {
        let clearance = 0;

        carousel.querySelectorAll('.slideshow__slide').forEach((slide) => {
          const content = slide.querySelector('.dac-slide-content');

          if (content) {
            clearance = Math.max(clearance, slide.getBoundingClientRect().bottom - content.getBoundingClientRect().top);
          }
        });

        this.style.setProperty('--dac-slideshow-logo-clearance', `${Math.ceil(clearance)}px`);
      };

      const observer = new ResizeObserver(update);

      observer.observe(carousel);
      carousel.querySelectorAll('.dac-slide-content').forEach((content) => observer.observe(content));
      this.#lifecycle.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }

    // Wild eyes starts its reveal about 0.7s after the hero photo is on screen. This slideshow settles straight away, so
    // wait for the first slide photo, the logo itself and the slide text / buttons to be ready, then for the delay set on
    // the section (data-delay, ms).
    async #start() {
      window.clearTimeout(this.#timer);

      if (this.#started) {
        return;
      }

      this.#started = true;

      const images = [
        this.closest('slideshow-carousel')?.querySelector('.slideshow__slide img'),
        this.querySelector('.dac-slideshow-logo__image')
      ].filter(Boolean);

      const imagesReady = Promise.all(images.map((image) => image.decode().catch(() => {})));

      // The slide text and buttons appear first (assets/dac-button-pairs.js), then the logo
      const buttonsReady = document.documentElement.hasAttribute('data-dac-buttons-ready')
        ? Promise.resolve()
        : new Promise((resolve) => document.addEventListener('dac:buttons-ready', resolve, { once: true }));

      await Promise.race([Promise.all([imagesReady, buttonsReady]), new Promise((resolve) => window.setTimeout(resolve, 3500))]);

      if (this.#lifecycle.signal.aborted) {
        return;
      }

      this.#timer = window.setTimeout(() => this.#markReady(), parseInt(this.dataset.delay ?? '700', 10) || 0);
    }

    disconnectedCallback() {
      this.#lifecycle?.abort();
      window.clearTimeout(this.#timer);
    }

    #markReady() {
      window.clearTimeout(this.#timer);

      if (this.classList.contains('is-ready')) {
        return;
      }

      const rightHalf = this.querySelector('.dac-slideshow-logo__pane--right .dac-slideshow-logo__media');
      rightHalf?.addEventListener('animationend', () => this.classList.add('is-done'), { once: true, signal: this.#lifecycle.signal });

      this.classList.add('is-ready');
    }
  }

  window.customElements.define('dac-slideshow-logo', DacSlideshowLogo);
}
